import { Box, Typography } from "@mui/material";
import FalhaCard, { FalhaCardProps } from "./FalhaCard";
import { MainContext } from "../../../../main/context/MainContextProvider";
import { useContext, useEffect } from "react";
import PrimaryBaseButton from "../../../../main/components/PrimaryBaseButton";
import { useState, useMemo } from "react";
import { Checkbox, FormControl, InputLabel, MenuItem, Select, Chip } from "@mui/material";
import { operationsApi } from "../../../../services/api";
import type { CriticalFailure } from "../../../../services/api/routes/operations";
import { SeverityTypesEnum } from "../../../alarms/types";

interface FalhasCriticasRecentesProps {
    clusterId?: string;
    usinaId?: number;
}

export default function FalhasCriticasRecentes({ clusterId, usinaId }: FalhasCriticasRecentesProps) {
    const PAGE_SIZE = 10;
    type SeverityFilter = "severe" | "medium" | "bland" | undefined;
    const { theme } = useContext(MainContext);
    const [selectedStatus, setSelectedStatus] = useState<string | undefined>();
    const [selectedSeverity, setSelectedSeverity] = useState<SeverityFilter>();
    const [selectedUG, setSelectedUG] = useState<string[]>([]);
    const [criticalFailures, setCriticalFailures] = useState<CriticalFailure[]>([]);
    const [loading, setLoading] = useState(false);
    const [allAreas, setAllAreas] = useState<string[]>([]);
    const [visibleItemsCount, setVisibleItemsCount] = useState(PAGE_SIZE);

    useEffect(() => {
        fetchCriticalFailures();
    }, [clusterId, usinaId]);

    useEffect(() => {
        setVisibleItemsCount(PAGE_SIZE);
    }, [clusterId, usinaId, selectedStatus, selectedSeverity, selectedUG]);

    const fetchCriticalFailures = async () => {
        try {
            setLoading(true);

            const response = await operationsApi.getRecentCriticalFailures({
                ClusterId: clusterId,
                UsinaId: usinaId,
            });

            const failures = response.criticalFailures || [];
            setCriticalFailures(failures);

            // Extrair áreas únicas
            const uniqueAreas = [...new Set(failures.map((f) => f.areaCode).filter(Boolean))];
            setAllAreas(uniqueAreas as string[]);
        } catch (error) {
            console.error('Erro ao buscar falhas críticas:', error);
            setCriticalFailures([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredFailures = useMemo(() => {
        let filtered = [...criticalFailures];

        if (selectedStatus) {
            filtered = filtered.filter(failure => {
                if (selectedStatus === "alarme") {
                    return failure.type?.toLowerCase().includes("alarme");
                } else if (selectedStatus === "evento") {
                    return failure.type?.toLowerCase().includes("evento");
                } else if (selectedStatus === "comando") {
                    return failure.type?.toLowerCase().includes("comando");
                }
                return false;
            });
        }

        if (selectedUG.length > 0) {
            filtered = filtered.filter(failure =>
                selectedUG.includes(failure.areaCode)
            );
        }

        if (selectedSeverity) {
            filtered = filtered.filter((failure) => {
                if (selectedSeverity === "severe") return failure.severity >= 3;
                if (selectedSeverity === "medium") return failure.severity === 2;
                return failure.severity <= 1;
            });
        }

        return filtered;
    }, [criticalFailures, selectedStatus, selectedSeverity, selectedUG]);

    const falhas: FalhaCardProps[] = useMemo(() => {
        return filteredFailures.map(failure => {
            let severity: SeverityTypesEnum;
            if (failure.severity >= 3) severity = SeverityTypesEnum.severe;
            else if (failure.severity === 2) severity = SeverityTypesEnum.medium;
            else severity = SeverityTypesEnum.bland;

            return {
                severity,
                usina: failure.usinaName ?? "-",
                descricao: `${failure.code ?? "-"} - ${failure.areaCode ?? "-"} - ${failure.equipmentIdentifier ?? "-"}`,
                detalhes: failure.message ?? "-",
                tempo: failure.alarmDuration,
                dataHoraInicio: failure.triggeredAt,
                dataHoraFim: failure.clearedAt
            };
        });
    }, [filteredFailures]);

    const visibleFalhas = useMemo(() => {
        return falhas.slice(0, visibleItemsCount);
    }, [falhas, visibleItemsCount]);

    const renderChipValue = (selected: string[]) => {
        const visibleChips = selected.slice(0, 2);
        const remainingChips = selected.length - visibleChips.length;
        return (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {visibleChips.map((value) => (
                    <Chip key={value} size="small" label={value} />
                ))}
                {remainingChips > 0 && (
                    <Chip key="more" size="small" label={`+${remainingChips}`} />
                )}
            </Box>
        );
    };

    const handleCheckboxToggle = (
        areaPart: string,
    ) => {
        const ugIsSelected = !!selectedUG.find((ug) => ug == areaPart);

        if (ugIsSelected) {
            setSelectedUG(selectedUG.filter((ug) => ug != areaPart))
        } else {
            setSelectedUG([...selectedUG, areaPart])
        }

    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, marginBottom: "16px", color: theme.primaryTextColor, textAlign: "left" }}>
                Falhas Críticas Recentes
            </Typography>
            <Box
                sx={{
                    display: "flex",
                    columnGap: 1,
                    flexDirection: "row",
                    alignItems: "center",
                    marginBottom: "12px"
                }}
            >
                <PrimaryBaseButton
                    isActive={selectedStatus === undefined}
                    onClick={() => {
                        setSelectedStatus(undefined);
                    }}
                    sx={{ height: "25px" }}
                    title="Todos"
                />
                <PrimaryBaseButton
                    isActive={selectedStatus === "alarme"}
                    onClick={() => {
                        setSelectedStatus("alarme");
                    }}
                    sx={{ height: "25px" }}
                    title="Alarmes"
                />
                <PrimaryBaseButton
                    isActive={selectedStatus === "evento"}
                    onClick={() => {
                        setSelectedStatus("evento");
                    }}
                    sx={{ height: "25px" }}
                    title="Eventos"
                />
                <PrimaryBaseButton
                    isActive={selectedStatus === "comando"}
                    onClick={() => {
                        setSelectedStatus("comando");
                    }}
                    sx={{ height: "25px" }}
                    title="Comandos"
                />
            </Box>
            <Box
                sx={{
                    display: "flex",
                    gap: "10px",
                    alignItems: "center",
                    marginBottom: "8px",
                    flexWrap: "wrap",
                }}
            >
                <FormControl
                    sx={{
                        minWidth: "200px",
                        height: "25px",
                    }}
                >
                    {selectedUG.length === 0 && (
                        <InputLabel
                            id="demo-multiple-chip-label"
                            sx={{
                                top: "50%",
                                transform: "translateY(-50%)",
                                left: 10,
                                pointerEvents: "none",
                                transition: "opacity 0.2s",
                                '&.Mui-focused': {
                                    opacity: 0,
                                }
                            }}
                        >
                            Área
                        </InputLabel>
                    )}
                    <Select
                        labelId="demo-multiple-chip-label"
                        id="demo-multiple-chip"
                        multiple
                        value={selectedUG}
                        renderValue={renderChipValue}
                        sx={{
                            fontSize: "14px",
                            width: "100%",
                            height: "25px",
                            borderRadius: "10px",
                            padding: "0",
                        }}
                    >
                        <Box
                            sx={{
                                maxHeight: "200px",
                                // marginLeft: "-10px",
                            }}
                        >
                            {allAreas.map((ug) => (
                                <MenuItem
                                    onClick={
                                        () =>
                                            handleCheckboxToggle(
                                                ug
                                            )
                                    }
                                    value={ug}>

                                    <Checkbox
                                        checked={selectedUG.includes(ug)}
                                        onChange={
                                            () =>
                                                handleCheckboxToggle(
                                                    ug
                                                )
                                        }
                                    />
                                    {ug}
                                </MenuItem>))}
                        </Box>
                    </Select>
                </FormControl>
                <FormControl
                    sx={{
                        minWidth: "200px",
                        height: "25px",
                    }}
                >
                    {!selectedSeverity && (
                        <InputLabel
                            id="severity-select-label"
                            sx={{
                                top: "50%",
                                transform: "translateY(-50%)",
                                left: 10,
                                pointerEvents: "none",
                                transition: "opacity 0.2s",
                                '&.Mui-focused': {
                                    opacity: 0,
                                }
                            }}
                        >
                            Severidade
                        </InputLabel>
                    )}
                    <Select
                        labelId="severity-select-label"
                        id="severity-select"
                        value={selectedSeverity ?? ""}
                        onChange={(event) => setSelectedSeverity((event.target.value || undefined) as SeverityFilter)}
                        sx={{
                            fontSize: "14px",
                            width: "100%",
                            height: "25px",
                            borderRadius: "10px",
                            padding: "0",
                        }}
                    >
                        <MenuItem value="">Todas</MenuItem>
                        <MenuItem value="severe">Severo</MenuItem>
                        <MenuItem value="medium">Médio</MenuItem>
                        <MenuItem value="bland">Brando</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ flex: 1, overflowY: "auto", paddingRight: "8px", maxHeight: "400px" }}>
                {loading ? (
                    <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor, textAlign: "center", padding: "20px" }}>
                        Carregando...
                    </Typography>
                ) : falhas.length === 0 ? (
                    <Typography sx={{ fontSize: "12px", color: theme.secondaryTextColor, textAlign: "center", padding: "20px" }}>
                        Nenhuma falha crítica encontrada
                    </Typography>
                ) : (
                    <>
                        {visibleFalhas.map((falha, index) => (
                            <FalhaCard key={index} {...falha} />
                        ))}
                        {visibleItemsCount < falhas.length && (
                            <Box sx={{ display: "flex", justifyContent: "center", marginTop: "10px", marginBottom: "8px" }}>
                                <PrimaryBaseButton
                                    isActive={false}
                                    onClick={() => setVisibleItemsCount((prev) => prev + PAGE_SIZE)}
                                    sx={{ height: "28px" }}
                                    title="Carregar mais"
                                />
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
